import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "@/i18n/I18nProvider";
import { COMPANY, CANONICAL_ORIGIN } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

interface SEOProps {
  routeKey: RouteKey;
  titleOverride?: string;
  description?: string;
}

/**
 * Minimal client-side SEO manager.
 * Sets <title>, description, canonical, Open Graph / Twitter tags and
 * JSON-LD (Organization + LocalBusiness + ContactPoint).
 * Canonical domain: https://bnm.ge only.
 */
export function SEO({ routeKey, titleOverride, description }: SEOProps) {
  const { t, locale } = useT();
  const { pathname } = useLocation();

  useEffect(() => {
    const routeMeta = t.routes[routeKey];
    const pageTitle = titleOverride ?? routeMeta.title;
    const composed =
      routeKey === "home"
        ? `${t.meta.company} — ${t.meta.tagline}`
        : `${pageTitle} · ${t.meta.company}`;

    document.title = composed;

    const desc =
      description ??
      ("lede" in routeMeta && routeMeta.lede
        ? (routeMeta.lede as string)
        : t.meta.tagline);

    const canonical = `${CANONICAL_ORIGIN}${pathname}`;

    setLink("canonical", canonical);
    setMeta("name", "description", desc);
    setMeta("property", "og:title", composed);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:site_name", COMPANY.legalName);
    setMeta("property", "og:locale", locale === "ka" ? "ka_GE" : "en_GB");
    setMeta("property", "og:image", `${CANONICAL_ORIGIN}/og-image.png`);
    setMeta("name", "twitter:image", `${CANONICAL_ORIGIN}/og-image.png`);
    setMeta("name", "twitter:title", composed);
    setMeta("name", "twitter:description", desc);
    setJsonLd();
  }, [routeKey, titleOverride, description, t, locale, pathname]);

  return null;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  addressLocality: COMPANY.address.city,
  addressRegion: "Tbilisi",
  addressCountry: "GE",
};

function setJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${CANONICAL_ORIGIN}/#organization`,
        name: COMPANY.legalName,
        legalName: COMPANY.legalName,
        url: CANONICAL_ORIGIN,
        logo: `${CANONICAL_ORIGIN}/images/brand/bnm-logo-square.png`,
        image: `${CANONICAL_ORIGIN}/og-image.png`,
        email: COMPANY.email,
        foundingDate: COMPANY.established,
        taxID: COMPANY.idCode,
        identifier: {
          "@type": "PropertyValue",
          propertyID: "Georgia company identification code",
          value: COMPANY.idCode,
        },
        address: POSTAL_ADDRESS,
        contactPoint: {
          "@type": "ContactPoint",
          email: COMPANY.email,
          contactType: "sales",
          availableLanguage: ["English", "Georgian"],
        },
      },
      {
        "@type": "LocalBusiness",
        "@id": `${CANONICAL_ORIGIN}/#localbusiness`,
        name: COMPANY.legalName,
        url: CANONICAL_ORIGIN,
        image: `${CANONICAL_ORIGIN}/og-image.png`,
        email: COMPANY.email,
        address: POSTAL_ADDRESS,
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: COMPANY.businessHours.weekdays.open,
            closes: COMPANY.businessHours.weekdays.close,
          },
        ],
      },
    ],
  };

  let el = document.getElementById("bnm-jsonld") as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "bnm-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
